import { useState, useEffect } from 'react';
import { divisionsAPI, seriesAPI } from '../../services/api';
import { PageHeader, SectionTitle, Field, StatusBadge, Modal, ConfirmDialog, Spinner, EmptyState } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const POOLS = ['A','B','C','D','E','F','G','H'];
const DIV_COLORS = ['var(--gold)','var(--blue)','var(--accent)','var(--purple)','var(--red)','var(--mid)'];

const EMPTY_FORM = {
  series_id: '', name: '', short_name: '', games_per_team: '',
  pools: [], promotion_rules: '', relegation_rules: '', level_order: ''
};

export default function DivisionsPage() {
  const { canEdit, isAdmin } = useAuthStore();
  const [seriesList, setSeriesList] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => { loadSeries(); }, []);
  useEffect(() => { if (selectedSeries) loadDivisions(selectedSeries); else setDivisions([]); }, [selectedSeries]);

  const loadSeries = async () => {
    try {
      const { data } = await seriesAPI.list();
      setSeriesList(data);
      if (data.length > 0) setSelectedSeries(data[0].id);
    } catch { toast.error('Failed to load series'); }
  };

  const loadDivisions = async (seriesId) => {
    setLoading(true);
    try {
      const { data } = await divisionsAPI.list(seriesId);
      setDivisions(data);
    } catch { toast.error('Failed to load divisions'); }
    finally { setLoading(false); }
  };

  const setField = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const togglePool = (pool) => {
    setForm(p => ({
      ...p,
      pools: p.pools.includes(pool) ? p.pools.filter(x => x !== pool) : [...p.pools, pool].sort()
    }));
  };

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, series_id: selectedSeries });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (d) => {
    setForm({
      series_id: d.series_id, name: d.name, short_name: d.short_name || '',
      games_per_team: String(d.games_per_team || ''), pools: d.pools || [],
      promotion_rules: d.promotion_rules || '', relegation_rules: d.relegation_rules || '',
      level_order: String(d.level_order || '')
    });
    setEditingId(d.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.series_id || !form.name || !form.games_per_team)
      return toast.error('Series, name and games per team are required');
    setSaving(true);
    try {
      const payload = {
        ...form,
        games_per_team: parseInt(form.games_per_team),
        level_order: form.level_order ? parseInt(form.level_order) : null
      };
      if (editingId) { await divisionsAPI.update(editingId, payload); toast.success('Division updated'); }
      else { await divisionsAPI.create(payload); toast.success('Division created'); }
      setShowForm(false);
      loadDivisions(selectedSeries);
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await divisionsAPI.delete(showDeleteConfirm);
      toast.success('Division deleted');
      setShowDeleteConfirm(null);
      loadDivisions(selectedSeries);
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const selectedSeriesName = seriesList.find(s => s.id === selectedSeries)?.name || '';

  return (
    <div>
      <PageHeader icon="◫" title="Divisions"
        subtitle="Divisions belong to a Series. Pools belong to a Division."
        actions={canEdit() && <button className="btn btn-primary" onClick={openCreate}>+ Add Division</button>}
      />

      {/* Series selector */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--mid)', flexShrink: 0 }}>Viewing series:</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {seriesList.map(s => (
            <button key={s.id}
              className={`btn btn-sm ${selectedSeries === s.id ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setSelectedSeries(s.id)}>
              {s.short_name} <span style={{ opacity: 0.6, marginLeft: 4, fontSize: 10 }}>{s.season_year}</span>
            </button>
          ))}
        </div>
        {selectedSeries && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--mid)' }}>
            {divisions.length} division{divisions.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Divisions list */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={28} /></div>
      ) : !selectedSeries ? (
        <EmptyState icon="◫" title="Select a series" description="Choose a series above to view its divisions." />
      ) : divisions.length === 0 ? (
        <EmptyState icon="◫" title="No divisions yet"
          description={`${selectedSeriesName} has no divisions. Add the first one.`}
          action={canEdit() && <button className="btn btn-primary" onClick={openCreate}>+ Add Division</button>}
        />
      ) : (
        <div>
          <SectionTitle style={{ marginBottom: 12 }}>{selectedSeriesName} — Divisions</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {divisions.map((d, i) => (
              <div key={d.id} className="card" style={{ padding: 16, borderLeft: `3px solid ${DIV_COLORS[i % DIV_COLORS.length]}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{d.name}</span>
                      {d.short_name && <span className="mono" style={{ fontSize: 11, color: 'var(--mid)' }}>{d.short_name}</span>}
                      <StatusBadge status={d.status || 'active'} />
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {d.pools && d.pools.length > 0
                        ? d.pools.map(p => <span key={p} className="tag tag-blue">Pool {p}</span>)
                        : <span className="tag tag-mid">No Pools</span>}
                      <span className="tag tag-green">{d.team_count || 0} teams</span>
                      <span className="tag tag-gold">{d.games_per_team} games/team</span>
                      {d.level_order && <span className="tag tag-purple">Level {d.level_order}</span>}
                    </div>
                    {(d.promotion_rules || d.relegation_rules) && (
                      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--mid)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {d.promotion_rules && <span>↑ {d.promotion_rules}</span>}
                        {d.relegation_rules && <span>↓ {d.relegation_rules}</span>}
                      </div>
                    )}
                  </div>
                  {canEdit() && (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 12 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(d)}>Edit</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => window.location.href = `/conditions?division=${d.id}`}>Conditions</button>
                      {isAdmin() && <button className="btn btn-danger btn-sm" onClick={() => setShowDeleteConfirm(d.id)}>Delete</button>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginTop: 20 }}>
            {[
              { label: 'Total Divisions', value: divisions.length, color: 'var(--accent)' },
              { label: 'Total Teams', value: divisions.reduce((a, d) => a + (parseInt(d.team_count) || 0), 0), color: 'var(--blue)' },
              { label: 'With Pools', value: divisions.filter(d => d.pools?.length > 0).length, color: 'var(--gold)' },
              { label: 'Avg Games/Team', value: divisions.length ? Math.round(divisions.reduce((a,d) => a + (parseInt(d.games_per_team)||0), 0) / divisions.length) : 0, color: 'var(--purple)' },
            ].map(s => (
              <div key={s.label} className="card stat-card">
                <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showForm && (
        <Modal title={editingId ? 'Edit Division' : 'Add Division'} onClose={() => setShowForm(false)} width={560}>
          <Field label="Series" required>
            <select className="input select" value={form.series_id} onChange={e => setField('series_id', e.target.value)}>
              <option value="">Select series...</option>
              {seriesList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12 }}>
            <Field label="Division Name" required>
              <input className="input" placeholder="e.g. Premier" value={form.name} onChange={e => setField('name', e.target.value)} />
            </Field>
            <Field label="Short Name">
              <input className="input" placeholder="e.g. PREM" value={form.short_name} onChange={e => setField('short_name', e.target.value.toUpperCase())} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Games Per Team" required>
              <input className="input" placeholder="e.g. 10" value={form.games_per_team} onChange={e => setField('games_per_team', e.target.value)} />
            </Field>
            <Field label="Level Order" note="1 = top division">
              <input className="input" placeholder="e.g. 1" value={form.level_order} onChange={e => setField('level_order', e.target.value)} />
            </Field>
          </div>

          <Field label="Pools / Groups" note="Click to toggle pools for this division">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              {POOLS.map(p => (
                <div key={p} onClick={() => togglePool(p)}
                  style={{
                    width: 38, height: 38, borderRadius: 7, cursor: 'pointer',
                    border: `1.5px solid ${form.pools.includes(p) ? 'var(--accent)' : 'var(--border)'}`,
                    background: form.pools.includes(p) ? 'rgba(0,200,150,.12)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700,
                    color: form.pools.includes(p) ? 'var(--accent)' : 'var(--dim)',
                    transition: 'all .15s'
                  }}>
                  {p}
                </div>
              ))}
              {form.pools.length > 0 && (
                <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'center' }}
                  onClick={() => setField('pools', [])}>Clear</button>
              )}
            </div>
            {form.pools.length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 6 }}>
                {form.pools.length} pool{form.pools.length !== 1 ? 's' : ''} selected: {form.pools.map(p => `Pool ${p}`).join(', ')}
              </div>
            )}
          </Field>

          <Field label="Promotion Rules" note="Optional">
            <input className="input" placeholder="e.g. Top 2 promote to Division I"
              value={form.promotion_rules} onChange={e => setField('promotion_rules', e.target.value)} />
          </Field>

          <Field label="Relegation Rules" note="Optional">
            <input className="input" placeholder="e.g. Bottom 2 relegated to Division II"
              value={form.relegation_rules} onChange={e => setField('relegation_rules', e.target.value)} />
          </Field>

          <hr className="divider" />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><Spinner size={14} /> Saving...</> : editingId ? '✓ Update Division' : '+ Add Division'}
            </button>
          </div>
        </Modal>
      )}

      {showDeleteConfirm && (
        <ConfirmDialog danger
          message="Delete this division? All teams and fixtures in this division will also be removed. This cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(null)}
        />
      )}
    </div>
  );
}